"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { useRouter } from "next/navigation";
import { CTAButton, SectionHeader, MatchScore, type MatchScoreContent } from "fansunited-sports-ui";
import { BettingOdds } from "@/features/odds/components/BettingOdds";
import type MatchFullModel from "fansunited-sdk-esm/Core/Namespaces/Football/Models/Match/MatchFullModel";
import { formatDateDDMM, formatUpcomingMatchTime } from "@/utilities/formatting/date";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { usePlayerPageData } from "../context/PlayerPageDataContext";
interface PlayerMatchHighlightProps {
  type: "latest" | "upcoming";
}

/**
 * Maps SDK MatchFullModel to MatchScoreContent for the UI component
 */
function mapMatchToUI(match: MatchFullModel, isLatest: boolean, options?: {
  onClick?: () => void;
}): MatchScoreContent {
  const kickoffDate = new Date(match.kickoffAt);
  return {
    id: match.id,
    onClick: options?.onClick,
    competitorOne: {
      id: match.homeTeam.id,
      name: match.homeTeam.name,
      shortName: match.homeTeam.shortName,
      logo: {
        url: match.homeTeam.assets?.logo || "",
        alt: match.homeTeam.name
      },
      ...(isLatest && match.scores?.ftScore && {
        score: {
          main: String(match.scores.ftScore.homeGoals ?? "0")
        }
      })
    },
    competitorTwo: {
      id: match.awayTeam.id,
      name: match.awayTeam.name,
      shortName: match.awayTeam.shortName,
      logo: {
        url: match.awayTeam.assets?.logo || "",
        alt: match.awayTeam.name
      },
      ...(isLatest && match.scores?.ftScore && {
        score: {
          main: String(match.scores.ftScore.awayGoals ?? "0")
        }
      })
    },
    status: isLatest ? "final" : "scheduled",
    metadata: {
      competition: match.context?.competition?.name || "",
      date: kickoffDate.toISOString().split("T")[0],
      ...(isLatest && {
        time: formatDateDDMM(kickoffDate)
      }),
      ...(!isLatest && {
        time: formatUpcomingMatchTime(kickoffDate)
      })
    }
  };
}
export const PlayerMatchHighlight: React.FC<PlayerMatchHighlightProps> = t0 => {
  const $ = _c(50);
  const {
    type
  } = t0;
  const isLatest = type === "latest";
  const {
    getNextMatch,
    getPreviousMatch
  } = usePlayerPageData();
  const labels = useLabels().playerPage.matches;
  const matchLabels = useLabels().matches;
  const nav = useNavigation();
  const router = useRouter();
  const {
    hasOdds
  } = useTenantSettings();
  let match;
  let matchHref;
  let t1;
  let t2;
  if ($[0] !== getNextMatch || $[1] !== getPreviousMatch || $[2] !== isLatest || $[3] !== labels.lastAppearance || $[4] !== labels.nextFixture || $[5] !== matchLabels || $[6] !== nav || $[7] !== router) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      match = isLatest ? getPreviousMatch() : getNextMatch();
      if (!match) {
        const headerLabel = isLatest ? labels.lastAppearance : labels.nextFixture;
        const emptyTitle = isLatest ? matchLabels.noLatestResultTitle : matchLabels.noNextMatchTitle;
        const emptySubtitle = isLatest ? matchLabels.noLatestResultSubtitle : matchLabels.noNextMatchSubtitle;
        let t3;
        if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = {
            typography: {
              name: "text-sm uppercase font-semibold text-foreground"
            }
          };
          $[12] = t3;
        } else {
          t3 = $[12];
        }
        let t4;
        if ($[13] !== headerLabel) {
          t4 = {
            name: headerLabel,
            styles: t3
          };
          $[13] = headerLabel;
          $[14] = t4;
        } else {
          t4 = $[14];
        }
        let t5;
        if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = {
            container: "mb-2"
          };
          $[15] = t5;
        } else {
          t5 = $[15];
        }
        let t6;
        if ($[16] !== t4) {
          t6 = <SectionHeader entity={t4} styles={t5} />;
          $[16] = t4;
          $[17] = t6;
        } else {
          t6 = $[17];
        }
        let t7;
        if ($[18] !== emptyTitle) {
          t7 = <p className="text-base font-medium text-foreground">{emptyTitle}</p>;
          $[18] = emptyTitle;
          $[19] = t7;
        } else {
          t7 = $[19];
        }
        let t8;
        if ($[20] !== emptySubtitle) {
          t8 = <p className="mt-1 text-sm text-muted-foreground">{emptySubtitle}</p>;
          $[20] = emptySubtitle;
          $[21] = t8;
        } else {
          t8 = $[21];
        }
        let t9;
        if ($[22] !== t7 || $[23] !== t8) {
          t9 = <div className="flex flex-1 flex-col items-center justify-center rounded-lg bg-card p-8 text-center shadow-md">{t7}{t8}</div>;
          $[22] = t7;
          $[23] = t8;
          $[24] = t9;
        } else {
          t9 = $[24];
        }
        let t10;
        if ($[25] !== t6 || $[26] !== t9) {
          t10 = <div className="flex h-full flex-col">{t6}{t9}</div>;
          $[25] = t6;
          $[26] = t9;
          $[27] = t10;
        } else {
          t10 = $[27];
        }
        t2 = t10;
        break bb0;
      }
      matchHref = nav.match(match.id);
      t1 = mapMatchToUI(match, isLatest, {
        onClick: !isLatest ? () => router.push(matchHref) : undefined
      });
    }
    $[0] = getNextMatch;
    $[1] = getPreviousMatch;
    $[2] = isLatest;
    $[3] = labels.lastAppearance;
    $[4] = labels.nextFixture;
    $[5] = matchLabels;
    $[6] = nav;
    $[7] = router;
    $[8] = match;
    $[9] = matchHref;
    $[10] = t1;
    $[11] = t2;
  } else {
    match = $[8];
    matchHref = $[9];
    t1 = $[10];
    t2 = $[11];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const matchScoreData = t1;
  const headerLabel_0 = isLatest ? labels.lastAppearance : labels.nextFixture;
  let t3;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      typography: {
        name: "text-sm uppercase font-semibold text-foreground"
      }
    };
    $[28] = t3;
  } else {
    t3 = $[28];
  }
  let t4;
  if ($[29] !== headerLabel_0) {
    t4 = {
      name: headerLabel_0,
      styles: t3
    };
    $[29] = headerLabel_0;
    $[30] = t4;
  } else {
    t4 = $[30];
  }
  let t5;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      container: "mb-2"
    };
    $[31] = t5;
  } else {
    t5 = $[31];
  }
  let t6;
  if ($[32] !== t4) {
    t6 = <SectionHeader entity={t4} styles={t5} />;
    $[32] = t4;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  let t7;
  if ($[34] !== hasOdds || $[35] !== isLatest || $[36] !== match) {
    t7 = !isLatest && hasOdds ? <BettingOdds matchId={match.id} markets={["1x2"]} market="FT_1X2" kickoffAt={match.kickoffAt} /> : undefined;
    $[34] = hasOdds;
    $[35] = isLatest;
    $[36] = match;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  let t8;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      container: "bg-transparent",
      background: "bg-transparent text-card-foreground",
      shadow: "md",
      hoverShadow: "md",
      borderColor: "border-muted-foreground/5",
      borderWidth: "0"
    };
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] !== isLatest || $[40] !== matchHref || $[41] !== matchLabels) {
    t9 = isLatest && {
      secondaryCta: <CTAButton text={matchLabels.matchDetails} href={matchHref} variant="outline" styles={{
        container: "bg-purple-600 p-1 text-sm border-none bg-muted"
      }} />
    };
    $[39] = isLatest;
    $[40] = matchHref;
    $[41] = matchLabels;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  let t10;
  if ($[43] !== matchScoreData || $[44] !== t7 || $[45] !== t9) {
    t10 = <div className="match-card-surface overflow-hidden"><MatchScore match={matchScoreData} variant="scoreCard" odds={t7} styles={t8} {...t9} /></div>;
    $[43] = matchScoreData;
    $[44] = t7;
    $[45] = t9;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  let t11;
  if ($[47] !== t10 || $[48] !== t6) {
    t11 = <div>{t6}{t10}</div>;
    $[47] = t10;
    $[48] = t6;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  return t11;
};